import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  WrenchScrewdriverIcon,
  CalendarIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const VesselMechanicalTab = ({ vessel, onVesselUpdate, user }) => {
  const [mechanicalItems, setMechanicalItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'engine',
    manufacturer: '',
    model: '',
    serial_number: '',
    part_number: '',
    location: '',
    installation_date: '',
    last_service_date: '',
    next_service_date: '',
    service_interval_hours: '',
    service_interval_months: '',
    current_hours: '',
    status: 'operational',
    fuel_type: '',
    horsepower: '',
    voltage: '',
    notes: '',
    maintenance_notes: ''
  });

  const categories = [
    { value: 'engine', label: 'Engine' },
    { value: 'transmission', label: 'Transmission' },
    { value: 'generator', label: 'Generator' },
    { value: 'watermaker', label: 'Watermaker' },
    { value: 'air_conditioning', label: 'Air Conditioning' },
    { value: 'refrigeration', label: 'Refrigeration' },
    { value: 'pump', label: 'Pump' },
    { value: 'thruster', label: 'Thruster' },
    { value: 'stabilizer', label: 'Stabilizer' },
    { value: 'hydraulic', label: 'Hydraulic System' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'other', label: 'Other' }
  ];

  const statuses = [
    { value: 'operational', label: 'Operational' },
    { value: 'needs_service', label: 'Needs Service' },
    { value: 'out_of_order', label: 'Out of Order' },
    { value: 'under_repair', label: 'Under Repair' },
    { value: 'replaced', label: 'Replaced' }
  ];

  const fuelTypes = [
    { value: 'diesel', label: 'Diesel' },
    { value: 'gasoline', label: 'Gasoline' },
    { value: 'electric', label: 'Electric' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'other', label: 'Other' }
  ];

  const voltages = [
    { value: '12v', label: '12V' },
    { value: '24v', label: '24V' },
    { value: '110v', label: '110V' },
    { value: '220v', label: '220V' },
    { value: '12v/24v', label: '12V/24V' },
    { value: '12v/110v', label: '12V/110V' }
  ];

  useEffect(() => {
    loadMechanicalItems();
  }, [vessel?.id]);

  const loadMechanicalItems = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${vessel.id}/mechanical`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch mechanical items');
      }
      
      const data = await response.json();
      setMechanicalItems(data.items || []);
    } catch (error) {
      console.error('Error loading mechanical items:', error);
      toast.error('Failed to load mechanical items');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${vessel.id}/mechanical`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add mechanical item');
      }

      toast.success('Mechanical item added successfully');
      setShowAddModal(false);
      resetForm();
      loadMechanicalItems();
    } catch (error) {
      console.error('Error adding mechanical item:', error);
      toast.error(error.message || 'Failed to add mechanical item');
    }
  };

  const handleEditItem = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${vessel.id}/mechanical/${selectedItem.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update mechanical item');
      }

      toast.success('Mechanical item updated successfully');
      setShowEditModal(false);
      setSelectedItem(null);
      loadMechanicalItems();
    } catch (error) {
      console.error('Error updating mechanical item:', error);
      toast.error(error.message || 'Failed to update mechanical item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this mechanical item?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${vessel.id}/mechanical/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete mechanical item');
      }

      toast.success('Mechanical item deleted successfully');
      loadMechanicalItems();
    } catch (error) {
      console.error('Error deleting mechanical item:', error);
      toast.error(error.message || 'Failed to delete mechanical item');
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name || '',
      category: item.category || 'engine',
      manufacturer: item.manufacturer || '',
      model: item.model || '',
      serial_number: item.serial_number || '',
      part_number: item.part_number || '',
      location: item.location || '',
      installation_date: item.installation_date || '',
      last_service_date: item.last_service_date || '',
      next_service_date: item.next_service_date || '',
      service_interval_hours: item.service_interval_hours || '',
      service_interval_months: item.service_interval_months || '',
      current_hours: item.current_hours || '',
      status: item.status || 'operational',
      fuel_type: item.fuel_type || '',
      horsepower: item.horsepower || '',
      voltage: item.voltage || '',
      notes: item.notes || '',
      maintenance_notes: item.maintenance_notes || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'engine',
      manufacturer: '',
      model: '',
      serial_number: '',
      part_number: '',
      location: '',
      installation_date: '',
      last_service_date: '',
      next_service_date: '',
      service_interval_hours: '',
      service_interval_months: '',
      current_hours: '',
      status: 'operational',
      fuel_type: '',
      horsepower: '',
      voltage: '',
      notes: '',
      maintenance_notes: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational': return 'bg-marine-100 text-marine-800';
      case 'needs_service': return 'bg-marine-50 text-marine-700';
      case 'out_of_order': return 'bg-gray-100 text-gray-800';
      case 'under_repair': return 'bg-marine-100 text-marine-600';
      case 'replaced': return 'bg-marine-50 text-marine-700';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'engine': return '🔧';
      case 'transmission': return '⚙️';
      case 'generator': return '⚡';
      case 'watermaker': return '💧';
      case 'air_conditioning': return '❄️';
      case 'refrigeration': return '🧊';
      case 'pump': return '🌊';
      case 'thruster': return '🚀';
      case 'stabilizer': return '⚖️';
      case 'hydraulic': return '🔩';
      case 'electrical': return '🔌';
      case 'plumbing': return '🚿';
      default: return '🔧';
    }
  };

  const isServiceDue = (item) => {
    if (!item.next_service_date) return false;
    const nextService = new Date(item.next_service_date);
    const now = new Date();
    return nextService <= now;
  };

  const renderForm = (onSubmit, title) => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                >
                  {categories.map(category => (
                    <option key={category.value} value={category.value}>{category.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Manufacturer</label>
                <input
                  type="text"
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Model</label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Serial Number</label>
                <input
                  type="text"
                  name="serial_number"
                  value={formData.serial_number}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Part Number</label>
                <input
                  type="text"
                  name="part_number"
                  value={formData.part_number}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Installation Date</label>
                <input
                  type="date"
                  name="installation_date"
                  value={formData.installation_date}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Service Date</label>
                <input
                  type="date"
                  name="last_service_date"
                  value={formData.last_service_date}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Next Service Date</label>
                <input
                  type="date"
                  name="next_service_date"
                  value={formData.next_service_date}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Service Interval (Hours)</label>
                <input
                  type="number"
                  name="service_interval_hours"
                  value={formData.service_interval_hours}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Service Interval (Months)</label>
                <input
                  type="number"
                  name="service_interval_months"
                  value={formData.service_interval_months}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Current Hours</label>
                <input
                  type="number"
                  name="current_hours"
                  value={formData.current_hours}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                >
                  {statuses.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Fuel Type</label>
                <select
                  name="fuel_type"
                  value={formData.fuel_type}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                >
                  <option value="">Select Fuel Type</option>
                  {fuelTypes.map(fuel => (
                    <option key={fuel.value} value={fuel.value}>{fuel.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Horsepower</label>
                <input
                  type="number"
                  name="horsepower"
                  value={formData.horsepower}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Voltage</label>
                <select
                  name="voltage"
                  value={formData.voltage}
                  onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                >
                  <option value="">Select Voltage</option>
                  {voltages.map(voltage => (
                    <option key={voltage.value} value={voltage.value}>{voltage.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                rows="3"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Maintenance Notes</label>
              <textarea
                name="maintenance_notes"
                value={formData.maintenance_notes}
                onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                rows="3"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setSelectedItem(null);
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
              >
                {selectedItem ? 'Update' : 'Add'} Item
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading mechanical items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Mechanical Systems</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Mechanical Item
        </button>
      </div>

      {/* Service Due Alerts */}
      {mechanicalItems.filter(isServiceDue).length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Service Due Alerts
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <ul className="list-disc list-inside space-y-1">
                  {mechanicalItems.filter(isServiceDue).map(item => (
                    <li key={item.id}>
                      {item.name} - Service due on {new Date(item.next_service_date).toLocaleDateString()}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mechanical Items List */}
      {mechanicalItems.length === 0 ? (
        <div className="text-center py-12">
          <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No mechanical items</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by adding a mechanical item.</p>
          <div className="mt-6">
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Mechanical Item
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mechanicalItems.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">{getCategoryIcon(item.category)}</div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{item.name}</h3>
                    <p className="text-sm text-gray-500">{item.manufacturer} {item.model}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {item.status.replace('_', ' ').toUpperCase()}
                      </span>
                      {isServiceDue(item) && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          SERVICE DUE
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => openEditModal(item)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteItem(item.id)}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                {item.location && (
                  <div className="flex items-center text-sm text-gray-600">
                    <DocumentTextIcon className="h-4 w-4 mr-2" />
                    Location: {item.location}
                  </div>
                )}
                {item.serial_number && (
                  <div className="text-sm text-gray-600">
                    <strong>Serial:</strong> {item.serial_number}
                  </div>
                )}
                {item.part_number && (
                  <div className="text-sm text-gray-600">
                    <strong>Part #:</strong> {item.part_number}
                  </div>
                )}
                {item.installation_date && (
                  <div className="flex items-center text-sm text-gray-600">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Installed: {new Date(item.installation_date).toLocaleDateString()}
                  </div>
                )}
                {item.last_service_date && (
                  <div className="flex items-center text-sm text-gray-600">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    Last Service: {new Date(item.last_service_date).toLocaleDateString()}
                  </div>
                )}
                {item.next_service_date && (
                  <div className="flex items-center text-sm text-gray-600">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Next Service: {new Date(item.next_service_date).toLocaleDateString()}
                  </div>
                )}
                {item.current_hours && (
                  <div className="text-sm text-gray-600">
                    <strong>Hours:</strong> {item.current_hours}
                  </div>
                )}
                {item.horsepower && (
                  <div className="text-sm text-gray-600">
                    <strong>HP:</strong> {item.horsepower}
                  </div>
                )}
              </div>

              {item.notes && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600">{item.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showAddModal && renderForm(handleAddItem, 'Add Mechanical Item')}
      {showEditModal && renderForm(handleEditItem, 'Edit Mechanical Item')}
    </div>
  );
};

export default VesselMechanicalTab;
